import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  fetchConsentData, 
  fetchPatientData, 
  fetchNextIpNo, 
  createConsent, 
  updateConsent,
  generateShareToken
} from "../api/consentApi";
import { mapBackendToForm, mapFormToBackend } from "../utils/mapping";
import { useFormValidation } from "./useFormValidation";
import { useNotification } from "../../../../contexts/NotificationContext";
import { DRAFT_KEYS, loadDraft, clearDraft } from "../../../../utils/draftUtils";
import { signatureDatetimeMap } from "../constants/options";

export const useConsentForm = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [success, setSuccess] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    ip_no: '',
    uhid_no: uhid_no || '',
    patient_name: '',
    age: '',
    sex: '',
    consultant: '',
    ward_bed_no: '',
    acknowledge_patient_name: '',
    anaesthesia_care_plan: '',
    general_anaesthesia: false,
    spinal_epidural_with_sedation: false,
    spinal_epidural_without_sedation: false,
    major_minor_nerve_with_sedation: false,
    major_minor_nerve_without_sedation: false,
    dr_name: '',
    dr_his_her: '',
    patient_signature: '',
    patient_signature_name: '',
    patient_signature_date_time: '',
    witness_signature: '',
    witness_name: '',
    witness_signature_date_time: '',
    doctor_anaesthetist_signature: '',
    doctor_anaesthetist_signature_date_time: '',
    interpreter_signature: '',
    interpreter_name: '',
    interpreter_signature_date_time: '',
    unable_consent_reason: '',
    attender_name: '',
    patient_rep_signature: '',
    patient_rep_signature_date_time: '',
    patient_rep_witness_signature: '',
    patient_rep_witness_name: '',
    patient_rep_witness_date_time: '',
    patient_rep_doctor_anaesthetist_sign: '',
    patient_rep_doctor_anaesthetist_sign_date_time: '',
    patient_rep_interpreter_signature: '',
    patient_rep_interpreter_name: '',
    patient_rep_interpreter_signature_date_time: '',
    doctor_anaesthetist_name: '',
    patient_rep_name: '',
    patient_rep_doctor_anaesthetist_name: ''
  });

  const { errors, clearError, validateSubmit } = useFormValidation(formData);

  // Fetch Patient Data
  const { data: patientInfo } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  // Fetch Next IP No (if new form)
  const { data: nextIpData } = useQuery({
    queryKey: ['nextIpNo'],
    queryFn: fetchNextIpNo,
    enabled: !success
  });

  // Fetch Existing Consent Data
  const { data: existingData, isLoading: dataLoading } = useQuery({
    queryKey: ['consentAnaesthesia', uhid_no],
    queryFn: () => fetchConsentData(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  // Update form with fetched data
  useEffect(() => {
    if (existingData && Object.keys(existingData).length > 0) {
      const mapped = mapBackendToForm(existingData);
      setFormData(prev => ({ ...prev, ...mapped }));
      setSuccess(true);
    }
  }, [existingData]);

  // Update form with patient info
  useEffect(() => {
    if (patientInfo) {
      setFormData(prev => ({
        ...prev,
        patient_name: patientInfo.name,
        age: patientInfo.age,
        sex: patientInfo.sex
      }));
    }
  }, [patientInfo]);

  // Update form with next IP No
  useEffect(() => {
    if (nextIpData && !success) {
      setFormData(prev => ({ ...prev, ip_no: nextIpData.next_ip_no }));
    }
  }, [nextIpData, success]);

  // Handle draft
  useEffect(() => {
    const draft = loadDraft(DRAFT_KEYS.CONSENT_FOR_ANAESTHESIA, uhid_no);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid_no]);

  const handleChange = useCallback((e) => {
    const { name, type, checked, value } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      clearError(name);
    }
  }, [errors, clearError]);

  const handleNameChange = useCallback((e) => {
    const { name, value } = e.target;
    const regex = /^[a-zA-Z\s.-]*$/;
    if (value === '' || regex.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) {
        clearError(name);
      }
    }
  }, [errors, clearError]);

  const handleSignatureOpen = (signatureKey) => {
    setCurrentSignatureRole(signatureKey);
    const datetimeKey = signatureDatetimeMap[signatureKey];
    if (datetimeKey) {
      const now = new Date().toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, [datetimeKey]: now }));
    }
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = (signatureDataURL) => {
    setFormData(prev => ({
      ...prev,
      [currentSignatureRole]: signatureDataURL.split(',')[1]
    }));
    setSignatureDialogOpen(false);
  };

  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updateConsent(uhid_no, data);
      } else {
        return createConsent(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success 
        ? "Consent Form updated successfully!" 
        : "Consent Form submitted successfully!");
      
      queryClient.invalidateQueries(['consentAnaesthesia', uhid_no]);
      setSuccess(true);
      clearDraft(DRAFT_KEYS.CONSENT_FOR_ANAESTHESIA, uhid_no);
      navigate(`/mainPage/${uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    },
    onError: (error) => {
      console.error('Error submitting the form!', error);
      showNotification('error', "Error in submitting form!");
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mutation.isLoading) return;

    if (!validateSubmit()) {
      return;
    }

    const backendData = mapFormToBackend(formData);
    mutation.mutate(backendData);
  };

  const handleShare = async () => {
    try {
      const { token } = await generateShareToken(uhid_no);
      const link = `${window.location.origin}/consentForAnaestnesia/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the consent for anaesthesia form:\n\n${link}`;

      await navigator.clipboard.writeText(link);
      showNotification('info', "Secure link copied to clipboard!");

      const phoneNumber = '+916379605575'; 
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      showNotification('error', "Failed to generate shareable link");
    }
  };

  return {
    formData,
    setFormData,
    errors,
    loading: dataLoading || mutation.isLoading,
    success,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare
  };
};
